import { FormEvent, useState } from 'react';

import { useExperienceState } from '@cctv/contexts/ExperienceStateContext';
import { Button } from '@cctv/core/Button/Button';
import { Option } from '@cctv/core/Option/Option';
import { useSubmitPollResponse } from '@cctv/hooks/useSubmitPollResponse';
import { JsonValue, PollPayload } from '@cctv/types';
import { getFormData } from '@cctv/utils';

import styles from './Poll.module.scss';

interface PollProps extends PollPayload {
  blockId?: string;
  responses?: {
    total: number;
    user_responded?: boolean;
    user_response?: {
      id: string;
      answer: { selectedOptions?: string[] };
    } | null;
    aggregate?: Record<string, number>;
  };
  disabled?: boolean;
  viewContext?: 'participant' | 'monitor' | 'manage';
}

function selectionLabels(selected: JsonValue | undefined): string[] | null {
  if (!selected) return null;
  return Array.isArray(selected) ? selected.map((option) => String(option)) : [String(selected)];
}

export default function Poll({
  question,
  options,
  pollType,
  blockId,
  responses,
  disabled = false,
  viewContext = 'participant',
}: PollProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { submitPollResponse, error } = useSubmitPollResponse();
  const { submissionState } = useExperienceState();
  const submission = blockId ? submissionState[blockId] : undefined;
  const userResponded = !!submission || !!responses?.user_responded;

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = getFormData<{ selectedOptions: string[] }>(e.currentTarget);
    const selectedOptions = formData.selectedOptions;

    const actualBlockId = blockId || new URLSearchParams(window.location.search).get('blockId');

    if (!actualBlockId) {
      console.error('No block ID found');
      return;
    }

    if (!selectedOptions) {
      console.error('No selected options found');
      return;
    }

    setIsSubmitting(true);

    const response = await submitPollResponse({
      blockId: actualBlockId,
      answer: {
        selectedOptions: selectedOptions,
        submittedAt: new Date().toISOString(),
      },
    });

    if (!response?.success) {
      setIsSubmitting(false);
    }
  };

  if (userResponded) {
    const selected =
      selectionLabels(submission?.answer?.selectedOptions) ??
      selectionLabels(responses?.user_response?.answer?.selectedOptions);
    const displayValue = selected
      ? selected.join(', ')
      : 'You have already responded to this poll.';

    return (
      <div className={styles.submittedValue}>
        <p className={styles.legend}>{question}</p>
        <p className={styles.value}>{displayValue}</p>
      </div>
    );
  }

  if (isSubmitting) {
    return (
      <div className={styles.submittedValue}>
        <p className={styles.legend}>{question}</p>
        <p className={styles.value}>Submitting...</p>
      </div>
    );
  }

  if (viewContext === 'monitor') {
    return (
      <div className={styles.submittedValue}>
        <p className={styles.legend}>{question}</p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit}>
      <fieldset disabled={disabled} className={styles.fieldset}>
        <legend className={styles.legend}>{question}</legend>
        {error && <p className={styles.error}>{error}</p>}
        {options.map((option) => (
          <Option
            allowMultiple={pollType === 'multiple'}
            key={option}
            option={option}
            name="selectedOptions"
            disabled={disabled}
          />
        ))}
        <Button type="submit">Submit</Button>
      </fieldset>
    </form>
  );
}
